import { Box, Chip, FormControl, InputLabel, MenuItem, OutlinedInput, Select, Grid, Divider } from "@mui/material";
import React from "react";
import { ToolbarFieldConfig, TFilterFieldsLocal } from "../ToolbarSinisterGeneric";

const SUBSTATUS_VALUES = ["preventive", "judicial", "extrajudicial"];

type Props = {
	field: ToolbarFieldConfig;
	filters: TFilterFieldsLocal;
	setFilter: (key: string, value: any) => void;
	getLabelFromValue: (value: string, field?: ToolbarFieldConfig) => string;
	onChangeExternal?: (value: any, filters: TFilterFieldsLocal, setFilter: (key: string, value: any) => void) => void;
};

export function renderSelectField({ field, filters, setFilter, getLabelFromValue, onChangeExternal }: Props): React.ReactNode {
	const value = filters[field.key] ?? field.defaultValue ?? "";
	const isMultiple = !!field.extraProps?.multiple;

	return (
		<Grid item key={field.key}>
			<FormControl size="small" fullWidth {...field.extraProps}>
				<InputLabel>{field.label}</InputLabel>
				<Select
					multiple={isMultiple}
					label={field.label}
					value={isMultiple ? (Array.isArray(value) ? value : []) : typeof value === "string" ? value : ""}
					onChange={(e) => {
						if (isMultiple) {
							let incoming = e.target.value as string[];
							let newValue = incoming.map((v) => v.trim());

							// Precisa saber QUAL item foi tocado nesse clique (comparando com a seleção anterior),
							// não só "o resultado final inclui Todos e mais alguém" — isso não distingue "acabei de
							// clicar em Todos" de "Todos já estava selecionado e cliquei em outra coisa", e as duas
							// situações pedem comportamentos opostos.
							const prevValue: string[] = Array.isArray(filters[field.key]) ? filters[field.key] : [];
							const todosJustToggledOn = newValue.includes("Todos") && !prevValue.includes("Todos");
							const todosJustToggledOff = !newValue.includes("Todos") && prevValue.includes("Todos");

							if (todosJustToggledOn) {
								// Clicou em "Todos" (havendo ou não outros marcados) => mantém só "Todos"
								newValue = ["Todos"];
							} else if (!todosJustToggledOff && newValue.includes("Todos") && newValue.some((v) => v !== "Todos")) {
								// "Todos" já estava marcado e o clique foi em outra opção => sai do modo "Todos"
								newValue = newValue.filter((v) => v !== "Todos");
							}

							if (newValue.includes("according")) {
								newValue = newValue.filter((v) => v !== "according");
								const alreadyAllIncluded = SUBSTATUS_VALUES.every((s) => newValue.includes(s));
								newValue = alreadyAllIncluded
									? newValue.filter((v) => !SUBSTATUS_VALUES.includes(v))
									: Array.from(new Set([...newValue, ...SUBSTATUS_VALUES]));
							}

							if (newValue.length === 0) {
								// Fallback pro default configurado do próprio campo (ex: sinistro cai em "open"/Abertos,
								// Acordos cai em "Todos") — não fazia sentido esse componente genérico assumir "open"
								// pra toda tela que o usa.
								newValue = Array.isArray(field.defaultValue) ? field.defaultValue : field.defaultValue ? [field.defaultValue] : ["Todos"];
							}

							setFilter(field.key, newValue);
							onChangeExternal?.(newValue, filters, setFilter);
							field.onChange?.(newValue, filters, setFilter);
						} else {
							const newValue = (e.target.value as string).trim();
							setFilter(field.key, newValue);
							onChangeExternal?.(newValue, filters, setFilter);
							field.onChange?.(newValue, filters, setFilter);
						}
					}}
					input={<OutlinedInput label={field.label} />}
					renderValue={(selected) =>
						isMultiple ? (
							<Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
								{(selected as string[]).map((val) => (
									<Chip key={val} label={getLabelFromValue(val, field).trim()} size="small" />
								))}
							</Box>
						) : (
							getLabelFromValue(selected as string, field)
						)
					}
				>
					{field.options?.map((opt) => (
						<MenuItem
							key={opt.value}
							value={opt.value}
							disabled={opt.isGroup}
							sx={{
								fontWeight: opt.isGroup ? "bold" : "normal",
								fontStyle: opt.isGroup ? "italic" : "normal",
								color: opt.isGroup ? "text.secondary" : "inherit",
								pl: opt.group === "according" ? 4 : 2,
								borderTop: opt.isGroup ? 1 : 0,
							}}
						>
							{opt.label}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</Grid>
	);
}
